// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A filter menu component for asset functional status selection.
 * This module provides a controlled interface for selecting
 * multiple asset functional statuses from a predefined list.
 * It integrates with the standard filter menu component and
 * supports customizable labels and options.
 *
 * Key features:
 * - Status selection
 * - Multiple selection
 * - Label customization
 * - Option overrides
 * - State management
 */
import { FilterMenu } from '@/components/filter-menu';
import {
  ASSET_FUNCTIONAL_STATUSES,
  ASSET_FUNCTIONAL_STATUS_LABELS,
  AssetFunctionalStatus,
} from '@/constants/asset-functional-status';

/**
 * Default label text for the filter menu button.
 */
const DEFAULT_LABEL = 'Status';

/**
 * Props for configuring the AssetFunctionalStatusFilterMenu component.
 *
 * @interface Props
 */
interface Props {
  /**
   * Currently selected functional statuses.
   * Empty array means no statuses are selected.
   */
  value: AssetFunctionalStatus[];

  /**
   * Custom label for the filter menu button.
   * Displayed when menu is closed.
   *
   * @default "Status"
   */
  label?: string;

  /**
   * Optional override for status options.
   * Allows customizing available choices.
   *
   * @default ["FUNCTIONAL", "NON_FUNCTIONAL"]
   */
  options?: AssetFunctionalStatus[];

  /**
   * Callback fired when selection changes.
   * Receives updated array of selected statuses.
   *
   * @param value - New array of selected statuses
   */
  onChange: (value: AssetFunctionalStatus[]) => void;
}

/**
 * A filter menu component for asset functional status selection.
 * Provides a controlled interface for selecting multiple asset
 * functional statuses from a predefined list. Integrates with
 * the standard filter menu component for consistent UX.
 *
 * Features:
 * - Status selection
 * - Multiple selection
 * - Label customization
 * - Option overrides
 * - State management
 * - Consistent UX
 *
 * Usage example:
 * ```tsx
 * function AssetList() {
 *   const [statuses, setStatuses] = useState<AssetFunctionalStatus[]>([]);
 *
 *   return (
 *     <AssetFunctionalStatusFilterMenu
 *       value={statuses}
 *       onChange={setStatuses}
 *       label="Filter by status"
 *     />
 *   );
 * }
 * ```
 */
export function AssetFunctionalStatusFilterMenu(props: Props) {
  // Use default or custom values
  const label = props.label ?? DEFAULT_LABEL;
  const options = props.options ?? ASSET_FUNCTIONAL_STATUSES;

  // Map statuses to filter menu options
  const filterMenuOptions = options.map((status) => ({
    id: status,
    label: ASSET_FUNCTIONAL_STATUS_LABELS[status],
    isActive: props.value.includes(status),
  }));

  /**
   * Handles option selection changes.
   * Updates the selected statuses array based on selection.
   *
   * @param value - The status being toggled
   * @param isActive - Whether the status is being selected or deselected
   */
  const handleChangeOption = (
    value: AssetFunctionalStatus,
    isActive: boolean
  ) => {
    if (isActive) {
      return props.onChange([...props.value, value]);
    }

    props.onChange(props.value.filter((status) => status !== value));
  };

  return (
    <FilterMenu
      label={label}
      options={filterMenuOptions}
      onChangeOption={handleChangeOption}
    />
  );
}
